import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FolderOpen, Search, Plus, Trash2, Package, AlertTriangle, AlertCircle, CheckCircle, Calendar, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Filter, X, XCircle, LayoutGrid, Shield, Layers, FileText, Clock, List, LayoutList, Tag, TrendingUp, Box, Palette } from 'lucide-react';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import { QawaemTableStickyActionsTd, mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import { ensureStatusBeforeActions } from '../../shared/utils/erpListColumnOrder';
import { useQawaemTableColumnVisibility, withQawaemActionsColumn, countVisibleTableCols } from '../../shared/hooks/useQawaemTableColumnVisibility';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, exactColumnRule, textColumnRule } from '../../shared/utils/columnHeaderFilters';
import { useSettings } from '../../core/contexts/SettingsContext';
import { printListPdfWithInvoiceTemplate } from './utils/templateListPdfExport';
import localApiAdapter from '../../core/services/localApiAdapter';
import { notifyCatalogCategoriesChanged, categoryRecordMatchesRef, CATALOG_CATEGORIES_REFRESH_EVENT } from '../articles/articleCatalogCategories';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { CustomSelect } from '../../shared/components/custom';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import fr from 'date-fns/locale/fr';
import arLocale from 'date-fns/locale/ar-SA';
import 'react-datepicker/dist/react-datepicker.css';
import '../../shared/styles/GPSTrackingDatePicker.css';
import FacturationViewModeToggle from './components/FacturationViewModeToggle';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import { getBrandLocale, getBrandDatePickerLocale } from '../../shared/utils/brandCurrency';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { shouldShowCardsToolbarSearch } from '../../shared/utils/listPageSearchVisibility';
import FacturationListPageLayout, {
  facturationTableHeadClass,
  facturationTableScrollClass,
} from './components/FacturationListPageLayout';
import { erpFormLabelClass, ERP_MODAL_FORM_CLASS } from './components/articleFormFieldClasses';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import {
  buildServiceStatusBadgeOptions,
  buildServiceStatusColumnFilterOptions,
  buildServiceStatusSelectOptions,
  categoryStatusFromRecord,
  getCatalogStatusLabel,
} from '../articles/articleCatalogStatusOptions';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { AppConfirmDialog } from '../../shared/components/AppConfirmDialog';
import { useCrudDeleteConfirm } from '../../shared/hooks/useCrudDeleteConfirm';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import { generateEntityReference, getEntityPrefix } from '../../shared/utils/generateEntityReference';
import { useArticlesConfig } from '../articles/useArticlesConfig';
import { useFacturationConfig } from './useFacturationConfig';
import FacturationListPaginationBar from './components/FacturationListPaginationBar';
import {
  useFacturationListPagination,
  useFacturationListViewMode,
} from './facturationListPageUtils';

registerLocale('fr', fr);
registerLocale('ar', arLocale);

// Inline edit cell for Categories - MUST stay outside Categories
function CategoryEditableCell({
  recordId, field, value, className = 'text-white font-semibold',
  isEditing, editValue, onEditValueChange, onStartEdit, onSave, onCancel,
  readOnly = true,
  displayValue,
}) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const skipBlurSaveRef = useRef(false);

  useEffect(() => {
    if (readOnly || !isEditing || !inputRef.current) return;
    const el = inputRef.current;
    requestAnimationFrame(() => {
      if (!inputRef.current) return;
      el.focus();
      const len = el.value.length;
      try { el.setSelectionRange(len, len); } catch {}
    });
  }, [isEditing, readOnly]);

  if (readOnly) {
    return <span className={className}>{(displayValue ?? value) || '-'}</span>;
  }

  if (isEditing) {
    return (
      <input
        ref={inputRef} type="text" value={editValue}
        onChange={(e) => onEditValueChange(e.target.value)}
        onBlur={(e) => {
          if (skipBlurSaveRef.current) { skipBlurSaveRef.current = false; return; }
          onSave(recordId, field, e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); skipBlurSaveRef.current = true; onSave(recordId, field, e.currentTarget.value); }
          else if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
        }}
        onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}
        className={`${className} bg-transparent border-none outline-none focus:outline-none w-full px-0`}
        style={{ caretColor: 'white' }} autoComplete="off" spellCheck={false}
      />
    );
  }

  return (
    <span role="button" tabIndex={0}
      onClick={() => onStartEdit(recordId, field, value)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onStartEdit(recordId, field, value); } }}
      className={`${className} cursor-pointer`} title={t('modules.article.pages.categories.click_to_edit')}
    >{value || '-'}</span>
  );
}

const CAT_I18N = 'modules.article.pages.categories';

const Categories = () => {
  const { isDashboardLightMode } = useGlobalTheme();
  const { t, i18n } = useTranslation();
  const tk = useCallback((key, opts) => t(`${CAT_I18N}.${key}`, opts), [t]);
  const qawaem = isSaudiBrand();
  const dateLocale = getBrandLocale(i18n.language);
  const datePickerLocale = getBrandDatePickerLocale();
  // Close dropdowns on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Type dropdown
      const isClickOnTypeButton = event.target.closest('[data-category-type-dropdown-button]');
      const isClickInTypeDropdown = event.target.closest('[data-category-type-dropdown]');
      if (!isClickOnTypeButton && !isClickInTypeDropdown) {
        setShowTypeDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const { settings } = useSettings();
  const articlesConfig = useArticlesConfig();
  const [searchTerm, setSearchTerm] = useState('');
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  
  // Inline editing states
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  
  const [formData, setFormData] = useState({ 
    category_number: '',
    name: '', 
    description: '', 
    type: '',
    prefix: '',
    status: 'active',
    active: true,
  });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const { dateRange, setDateRange } = useSaudiListDateRange();
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_categories');
  const [quickFilter, setQuickFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [isTypeFilterOpen, setIsTypeFilterOpen] = useState(false);
  
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const dateRangeRef = useRef(null);
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const typeDropdownRef = useRef(null);
  const typeFilterRef = useRef(null);
  
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const {
    deleteConfirm,
    closeDeleteConfirm,
    openDeleteOne,
    openBulkDelete,
    takePendingIds,
  } = useCrudDeleteConfirm();
  const entityLabel = tk('entity_label', { defaultValue: 'الفئة' });
  const entityLabelPlural = tk('entity_label_plural', { defaultValue: 'فئة' });

  useEffect(() => { loadCategories(); loadProducts(); }, []);
  useEffect(() => {
    const onCatalogRefresh = () => {
      loadCategories();
      loadProducts();
    };
    window.addEventListener('refresh-products', onCatalogRefresh);
    window.addEventListener(CATALOG_CATEGORIES_REFRESH_EVENT, onCatalogRefresh);
    return () => {
      window.removeEventListener('refresh-products', onCatalogRefresh);
      window.removeEventListener(CATALOG_CATEGORIES_REFRESH_EVENT, onCatalogRefresh);
    };
  }, []);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dateRangeRef.current && !dateRangeRef.current.contains(event.target)) setIsDateRangeOpen(false);
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
      if (typeFilterRef.current && !typeFilterRef.current.contains(event.target)) setIsTypeFilterOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadCategories = async () => {
    try {
      const response = await localApiAdapter.get('/categories');
      const rows = response?.data || response || [];
      setCategories(sortRecordsNewestFirst(Array.isArray(rows) ? rows : []));
    } catch (error) {
      console.error('Error loading categories:', error);
      setCategories([]);
    }
  };

  const loadProducts = async () => {
    try {
      const response = await localApiAdapter.get('/products');
      setProducts(response?.data || response || []);
    } catch (error) {
      console.error('Error loading products:', error);
      setProducts([]);
    }
  };

  const countLinkedForCategory = useCallback((category, kind) => {
    if (!category?.id) return 0;
    return products.filter((item) => {
      if (!categoryRecordMatchesRef(item.category, category.id, categories)) return false;
      if (kind === 'service') return item.type === 'service';
      return item.type !== 'service';
    }).length;
  }, [products, categories]);

  const categoriesEnriched = useMemo(
    () => categories.map((c) => {
      const product_linked_count = countLinkedForCategory(c, 'product');
      const service_linked_count = countLinkedForCategory(c, 'service');
      return {
        ...c,
        product_linked_count,
        service_linked_count,
        linked_count: product_linked_count + service_linked_count,
      };
    }),
    [categories, countLinkedForCategory],
  );

  const stats = {
    total: categories.length,
    active: categories.filter(c => c.active).length,
    inactive: categories.filter(c => !c.active).length,
    productCategories: categories.filter(c => c.type === 'product').length,
    totalProducts: products.filter((p) => p.type !== 'service').length,
    totalServices: products.filter((p) => p.type === 'service').length,
    totalLinkedItems: products.length,
  };

  const mainTabs = useMemo(() => ([
    { id: 'all_categories', name: tk('tab_overview'), icon: LayoutGrid },
    { id: 'par_type', name: tk('tab_by_type'), icon: Tag },
    { id: 'par_produits', name: tk('tab_by_products'), icon: Package }
  ]), [tk]);

  const quickFilterButtons = useMemo(() => ([
    { id: 'all', name: tk('filter_all'), icon: Layers },
    { id: 'active', name: tk('filter_active'), icon: CheckCircle },
    { id: 'inactive', name: tk('filter_inactive'), icon: XCircle },
    { id: 'empty', name: tk('filter_empty'), icon: AlertCircle }
  ]), [tk]);

  const categoriesDistribution = useMemo(() => ({
    all_categories: {
      title: tk('distribution_overview_title'),
      data: [
        { id: 'active', name: tk('distribution_active'), count: stats.active, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { id: 'inactive', name: tk('distribution_inactive'), count: stats.inactive, color: 'bg-gradient-to-r from-red-500 to-red-600' },
        { id: 'products', name: tk('distribution_products'), count: stats.productCategories, color: 'bg-gradient-to-r from-blue-500 to-blue-600' }
      ]
    },
    par_type: {
      title: tk('distribution_by_type_title'),
      data: [
        { id: 'products', name: tk('distribution_products'), count: categories.filter(c => c.type === 'product').length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { id: 'services', name: tk('distribution_services'), count: categories.filter(c => c.type === 'service').length, color: 'bg-gradient-to-r from-purple-500 to-purple-600' }
      ]
    },
    par_produits: {
      title: tk('distribution_by_products_title'),
      data: [
        { id: 'with_products', name: tk('distribution_with_products'), count: categoriesEnriched.filter(c => c.linked_count > 0).length, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { id: 'without_products', name: tk('distribution_without_products'), count: categoriesEnriched.filter(c => !c.linked_count).length, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600' }
      ]
    }
  }), [tk, stats, categories, categoriesEnriched]);

  const distributionInfo = categoriesDistribution[mainViewMode] || categoriesDistribution.all_categories;

  const resolveTypeLabel = (type) => {
    switch (type) {
      case 'product': return tk('type_product');
      case 'service': return tk('type_service');
      default: return type || '';
    }
  };

  const baseFilteredCategories = useMemo(
    () =>
      categoriesEnriched.filter((c) => {
        if (dateRange.start || dateRange.end) {
          const categoryDate = new Date(c.created_at);

          if (dateRange.start) {
            const startOfDay = new Date(dateRange.start);
            startOfDay.setHours(0, 0, 0, 0);
            if (categoryDate < startOfDay) return false;
          }

          if (dateRange.end) {
            const endOfDay = new Date(dateRange.end);
            endOfDay.setHours(23, 59, 59, 999);
            if (categoryDate > endOfDay) return false;
          }
        }

        if (typeFilter !== 'all' && c.type !== typeFilter) return false;

        if (
          searchTerm &&
          !(
            c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            c.description?.toLowerCase().includes(searchTerm.toLowerCase())
          )
        ) {
          return false;
        }

        if (mainViewMode === 'all_categories') {
          if (quickFilter === 'active' && !c.active) return false;
          if (quickFilter === 'inactive' && c.active) return false;
          if (quickFilter === 'empty' && c.linked_count > 0) return false;
        }

        if (activeDistributionFilter) {
          switch (mainViewMode) {
            case 'all_categories':
              if (activeDistributionFilter === 'active' && !c.active) return false;
              if (activeDistributionFilter === 'inactive' && c.active) return false;
              if (activeDistributionFilter === 'products' && c.type !== 'product') return false;
              break;
            case 'par_type':
              if (activeDistributionFilter === 'products' && c.type !== 'product') return false;
              if (activeDistributionFilter === 'services' && c.type !== 'service') return false;
              break;
            case 'par_produits': {
              const linkedCount = c.linked_count || 0;
              if (activeDistributionFilter === 'with_products' && linkedCount === 0) return false;
              if (activeDistributionFilter === 'without_products' && linkedCount > 0) return false;
              break;
            }
            default:
              break;
          }
        }

        return true;
      }),
    [categoriesEnriched, dateRange, typeFilter, searchTerm, mainViewMode, quickFilter, activeDistributionFilter],
  );

  const columnFilterRules = useMemo(
    () => [
      textColumnRule('category_number', (r) => r.category_number || ''),
      textColumnRule('created_at', (r) => (r.created_at ? new Date(r.created_at).toLocaleDateString(dateLocale) : '')),
      textColumnRule('name', (r) => r.name || ''),
      textColumnRule('description', (r) => r.description || ''),
      textColumnRule('type', (r) => resolveTypeLabel(r.type)),
      textColumnRule('product_count', (r) => String(r.product_linked_count || 0)),
      textColumnRule('service_count', (r) => String(r.service_linked_count || 0)),
      exactColumnRule('status', (r) => categoryStatusFromRecord(r)),
    ],
    [tk, dateLocale],
  );

  const filteredCategories = useMemo(
    () => applyColumnFilters(baseFilteredCategories, colFilters, columnFilterRules),
    [baseFilteredCategories, colFilters, columnFilterRules],
  );

  const categoriesFooter = useMemo(() => {
    let produitsLiees = 0;
    let servicesLiees = 0;
    let actives = 0;
    filteredCategories.forEach((c) => {
      produitsLiees += c.product_linked_count || 0;
      servicesLiees += c.service_linked_count || 0;
      if (c.active) actives += 1;
    });
    return { produitsLiees, servicesLiees, actives };
  }, [filteredCategories]);


  const generateCategoryNumber = useCallback(() => {
    const prefix = getEntityPrefix(settings, 'category', 'CAT');
    return generateEntityReference(prefix, categories, (c) => c.category_number);
  }, [categories, settings]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const categoryNumber = (formData.category_number || '').trim()
        || (articlesConfig.autoGenerateCategoryReference !== false ? generateCategoryNumber() : '');

      const submitData = { 
        category_number: categoryNumber,
        name: formData.name,
        description: formData.description || '',
        type: formData.type,
        status: formData.status,
        active: formData.status === 'active',
      };
      if (editingRecord) {
        await localApiAdapter.put(`/categories/${editingRecord.id}`, submitData);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post('/categories', submitData);
        notifyCreated(showNotify, entityLabel);
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, tk('save_error', { message: error.message || tk('unknown_error') }));
      return;
    } finally {
      resetForm();
      loadCategories();
      notifyCatalogCategoriesChanged();
    }
  };

  const handleEdit = (record) => {
    const status = categoryStatusFromRecord(record);
    setEditingRecord(record);
    setFormData({ 
      category_number: record.category_number || '',
      name: record.name || '', 
      description: record.description || '', 
      type: record.type || 'product',
      prefix: record.prefix || '',
      status,
      active: status === 'active',
    });
    setShowModal(true);
  };

  const openDeleteCategory = (id) => openDeleteOne(id);

  const openBulkDeleteCategories = () => {
    if (!selectedRecords.length) return;
    openBulkDelete(selectedRecords);
  };

  const executeDeleteCategories = async () => {
    const ids = takePendingIds();
    if (!ids.length) return;
    try {
      await Promise.all(ids.map((id) => localApiAdapter.delete(`/categories/${id}`)));
      setSelectedRecords((prev) => prev.filter((id) => !ids.includes(id)));
      loadCategories();
      notifyCatalogCategoriesChanged();
      if (ids.length > 1) {
        notifyBulkDeleted(showNotify, ids.length, entityLabelPlural);
      } else {
        notifyDeleted(showNotify, entityLabel);
      }
    } catch (error) {
      console.error('Error:', error);
      notifyError(
        showNotify,
        ids.length > 1
          ? tk('bulk_delete_error', { defaultValue: 'تعذّر حذف الفئات المحددة' })
          : tk('delete_error', { defaultValue: 'تعذّر حذف الفئة' }),
      );
    }
  };

  const exportHeaders = useMemo(() => ([
    { key: 'category_number', label: tk('col_category_number') },
    { key: 'name', label: tk('col_name') },
    { key: 'description', label: tk('col_description') },
    { key: 'active', label: tk('col_status') },
  ]), [tk]);

  const getExportStatusLabel = useCallback((record) => {
    return getCatalogStatusLabel(categoryStatusFromRecord(record), t);
  }, [tk]);

  const handleExportCSV = () => {
    const dataToExport = selectedRecords.length > 0 ? categories.filter(c => selectedRecords.includes(c.id)) : filteredCategories;
    exportToCSV(dataToExport, exportHeaders, `categories_${new Date().toISOString().split('T')[0]}.csv`);
    setShowExportDropdown(false);
  };

  const handleExportExcel = async () => {
    const dataToExport = selectedRecords.length > 0 ? categories.filter(c => selectedRecords.includes(c.id)) : filteredCategories;
    await exportToExcel(dataToExport, exportHeaders, `categories_${new Date().toISOString().split('T')[0]}.xlsx`, tk('export_sheet_name'));
    setShowExportDropdown(false);
  };

  const handleExportPDF = async () => {
    setIsGeneratingPDF(true);
    try {
      const dataToExport = selectedRecords.length > 0 ? categories.filter(c => selectedRecords.includes(c.id)) : filteredCategories;
      await printListPdfWithInvoiceTemplate({
        settings,
        rows: dataToExport,
        columns: [
          { label: tk('col_category_number'), value: (c) => c.category_number },
          { label: tk('col_name'), value: (c) => c.name },
          { label: tk('col_description'), value: (c) => c.description },
          { label: tk('col_status'), value: (c) => getExportStatusLabel(c) },
        ],
        listExportTitle: tk('export_list_title'),
        clientTitle: tk('export_client_title'),
        printDocumentTitle: `Categories - ${new Date().toISOString().split('T')[0]}`,
      });
      setShowExportDropdown(false);
    } catch (error) { console.error('PDF export error:', error); alert(tk('export_pdf_error')); } finally { setIsGeneratingPDF(false); }
  };

  const handleExportJSON = () => { const dataToExport = selectedRecords.length > 0 ? categories.filter(c => selectedRecords.includes(c.id)) : filteredCategories; const blob = new Blob([JSON.stringify(dataToExport, null, 2)], { type: 'application/json' }); const url = URL.createObjectURL(blob); const link = document.createElement('a'); link.href = url; link.download = `categories_${new Date().toISOString().split('T')[0]}.json`; document.body.appendChild(link); link.click(); document.body.removeChild(link); URL.revokeObjectURL(url); setShowExportDropdown(false); };
  const handleExportXML = () => { const dataToExport = selectedRecords.length > 0 ? categories.filter(c => selectedRecords.includes(c.id)) : filteredCategories; let xmlContent = '<?xml version="1.0" encoding="UTF-8"?>\n<categories>\n'; dataToExport.forEach(item => { xmlContent += '  <category>\n'; Object.keys(item).forEach(key => { xmlContent += `    <${key}>${item[key] || ''}</${key}>\n`; }); xmlContent += '  </category>\n'; }); xmlContent += '</categories>'; const blob = new Blob([xmlContent], { type: 'application/xml' }); const url = URL.createObjectURL(blob); const link = document.createElement('a'); link.href = url; link.download = `categories_${new Date().toISOString().split('T')[0]}.xml`; document.body.appendChild(link); link.click(); document.body.removeChild(link); URL.revokeObjectURL(url); setShowExportDropdown(false); };
  const handleExportHTML = () => {
    const dataToExport = selectedRecords.length > 0 ? categories.filter(c => selectedRecords.includes(c.id)) : filteredCategories;
    let htmlContent = `<!DOCTYPE html>\n<html>\n<head>\n  <meta charset="UTF-8">\n  <title>${tk('export_html_title')}</title>\n  <style>\n    body { font-family: Arial, sans-serif; margin: 20px; }\n    h1 { color: #1e40af; }\n    table { width: 100%; border-collapse: collapse; margin-top: 20px; }\n    th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }\n    th { background-color: #3b82f6; color: white; }\n    tr:nth-child(even) { background-color: #f9fafb; }\n  </style>\n</head>\n<body>\n  <h1>${tk('export_html_title')}</h1>\n  <p>${tk('export_date_label')} ${new Date().toLocaleDateString(dateLocale)}</p>\n  <p>${tk('export_total_line', { count: dataToExport.length })}</p>\n  <table>\n    <thead><tr><th>${tk('col_category_number')}</th><th>${tk('col_name')}</th><th>${tk('col_description')}</th><th>${tk('col_status')}</th></tr></thead>\n    <tbody>`;
    dataToExport.forEach(c => { htmlContent += `\n      <tr><td>${c.category_number || ''}</td><td>${c.name || ''}</td><td>${c.description || ''}</td><td>${getExportStatusLabel(c)}</td></tr>`; });
    htmlContent += `\n    </tbody>\n  </table>\n</body>\n</html>`;
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `categories_${new Date().toISOString().split('T')[0]}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setShowExportDropdown(false);
  };
  const handleExportTXT = () => {
    const dataToExport = selectedRecords.length > 0 ? categories.filter(c => selectedRecords.includes(c.id)) : filteredCategories;
    let txtContent = `${tk('export_list_title')}\n${tk('export_date_label')} ${new Date().toLocaleDateString(i18n.language?.startsWith('ar') ? 'ar-SA' : i18n.language || 'ar-SA')}\n${tk('export_total_line', { count: dataToExport.length })}\n\n${'='.repeat(100)}\n\n`;
    dataToExport.forEach(c => {
      txtContent += `${tk('export_field_category_number')} ${c.category_number || ''}\n${tk('export_field_name')} ${c.name || ''}\n${tk('export_field_description')} ${c.description || ''}\n${tk('export_field_status')} ${getExportStatusLabel(c)}\n${'-'.repeat(100)}\n\n`;
    });
    const blob = new Blob([txtContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `categories_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setShowExportDropdown(false);
  };

  const handleImportCSV = () => { triggerFileInput(async (file) => { try { const result = await importFromFile(file, exportHeaders, { requiredFields: ['name'] }); if (result.errors.length > 0) alert(tk('import_errors', { validCount: result.validCount, invalidCount: result.invalidCount, errors: result.errors.join('\n') })); else alert(tk('import_success', { count: result.validCount })); if (result.data.length > 0) setCategories([...categories, ...result.data.map((item, idx) => ({ ...item, id: Date.now() + idx }))]); setShowImportDropdown(false); } catch (error) { console.error('Import error:', error); alert(tk('import_file_error')); } }, '.csv'); };
  const handleImportExcel = () => { triggerFileInput(async (file) => { try { const result = await importFromFile(file, exportHeaders, { requiredFields: ['name'] }); if (result.errors.length > 0) alert(tk('import_errors', { validCount: result.validCount, invalidCount: result.invalidCount, errors: result.errors.join('\n') })); else alert(tk('import_success', { count: result.validCount })); if (result.data.length > 0) setCategories([...categories, ...result.data.map((item, idx) => ({ ...item, id: Date.now() + idx }))]); setShowImportDropdown(false); } catch (error) { console.error('Import error:', error); alert(tk('import_file_error')); } }, '.xlsx,.xls'); };
  const handleImportJSON = () => { triggerFileInput(async (file) => { try { const data = JSON.parse(await file.text()); if (!Array.isArray(data)) { alert(tk('import_json_invalid')); return; } const imported = data.map((item, idx) => ({ ...item, id: Date.now() + idx })); setCategories([...categories, ...imported]); alert(tk('import_success', { count: imported.length })); setShowImportDropdown(false); } catch (error) { console.error('JSON import error:', error); alert(tk('import_json_error')); } }, '.json'); };
  const handleImportXML = () => { triggerFileInput(async (file) => { try { const xmlDoc = new DOMParser().parseFromString(await file.text(), 'text/xml'); const nodes = xmlDoc.getElementsByTagName('category'); const imported = []; for (let i = 0; i < nodes.length; i++) { const node = nodes[i]; imported.push({ id: Date.now() + i, category_number: node.getElementsByTagName('category_number')[0]?.textContent || '', name: node.getElementsByTagName('name')[0]?.textContent || '', description: node.getElementsByTagName('description')[0]?.textContent || '', status: node.getElementsByTagName('status')[0]?.textContent || 'active' }); } setCategories([...categories, ...imported]); alert(tk('import_success', { count: imported.length })); setShowImportDropdown(false); } catch (error) { console.error('XML import error:', error); alert(tk('import_xml_error')); } }, '.xml'); };
  const handleImportTXT = () => { triggerFileInput(async (file) => { try { const lines = (await file.text()).split('\n').filter(line => line.trim()); const delimiter = lines[0].includes('\t') ? '\t' : ','; const headers = lines[0].split(delimiter).map(h => h.trim()); const imported = []; for (let i = 1; i < lines.length; i++) { const values = lines[i].split(delimiter); const cat = { id: Date.now() + i }; headers.forEach((header, index) => { cat[header.toLowerCase().replace(/\s+/g, '_')] = values[index]?.trim() || ''; }); imported.push(cat); } setCategories([...categories, ...imported]); alert(tk('import_success', { count: imported.length })); setShowImportDropdown(false); } catch (error) { console.error('TXT import error:', error); alert(tk('import_txt_error')); } }, '.txt,.tsv'); };

  const openNewCategoryModal = () => {
    setFormData({ 
      category_number: articlesConfig.autoGenerateCategoryReference !== false ? generateCategoryNumber() : '',
      name: '',
      description: '',
      type: 'product',
      prefix: '',
      status: 'active',
      active: true,
    });
    setEditingRecord(null);
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({ 
      category_number: '',
      name: '', 
      description: '', 
      type: '',
      prefix: '',
      status: 'active',
      active: true,
    });
    setEditingRecord(null);
    setShowModal(false);
  };

  
  const {
    facturationConfig,
    pageSize: facturationPageSize,
    listPage: facturationListPage,
    setListPage: setFacturationListPage,
    pagedItems,
    totalItems: facturationListTotal,
  } = useFacturationListPagination(filteredCategories);
  const [viewMode, setViewMode] = useFacturationListViewMode(facturationConfig);

  const filteredRecordIds = useMemo(() => filteredCategories.map((r) => r.id), [filteredCategories]);

  const allRowsSelected = filteredRecordIds.length > 0 && filteredRecordIds.every((id) => selectedRecords.includes(id));
  const someRowsSelected = selectedRecords.length > 0 && !allRowsSelected;

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredRecordIds);
    }
  };
  const handleSelectRecord = (id) => {
    setSelectedRecords((prev) => (prev.includes(id) ? prev.filter((rId) => rId !== id) : [...prev, id]));
  };

  const statusOptions = useMemo(() => buildServiceStatusBadgeOptions(t), [t]);

  const statusColumnFilterOptions = useMemo(() => buildServiceStatusColumnFilterOptions(t), [t]);

  const categoryModalStatusSelectOptions = useMemo(
    () => buildServiceStatusSelectOptions(t, { withIcons: true }),
    [t],
  );

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`/categories/${recordId}`, {
        status: newStatus,
        active: newStatus === 'active',
      });
      await loadCategories();
      notifyCatalogCategoriesChanged();
    } catch (error) {
      console.error('❌ Error updating status:', error);
    }
  };

  // Inline editing functions
  const startEditing = (recordId, field, currentValue) => {
    setEditingCell({ recordId, field });
    setEditValue(currentValue || '');
  };

  const cancelEditing = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const saveInlineEdit = async (recordId, field, value) => {
    const record = categories.find(c => c.id === recordId);
    if (!record) { cancelEditing(); return; }

    const norm = (v) => String(v ?? '').trim();
    const newVal = norm(value);
    const originalVal = norm(record[field]);
    if (newVal === originalVal) {
      cancelEditing();
      return;
    }
    if (field === 'name' && !newVal) {
      cancelEditing();
      return;
    }

    try {
      await localApiAdapter.put(`/categories/${recordId}`, { [field]: newVal });
      notifyUpdated(showNotify, entityLabel);
      await loadCategories();
      notifyCatalogCategoriesChanged();
      cancelEditing();
      setViewingRecord((prev) => (prev && prev.id === recordId ? { ...prev, [field]: newVal } : prev));
    } catch (error) {
      console.error('Error saving inline edit:', error);
      notifyError(showNotify, tk('save_error', { message: error.message || tk('unknown_error') }));
      cancelEditing();
    }
  };

  const getStatusBadge = (status) => {
    const value = status === true ? 'active' : status === false ? 'inactive' : (status || 'inactive');
    const config = statusOptions.find((s) => s.value === value) || statusOptions[0];
    const { label, icon: Icon, style } = config;
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${style}`}><Icon size={14} />{label}</span>;
  };

  const getTypeLabel = useCallback((type) => {
    switch(type) {
      case 'product': return tk('type_product');
      case 'service': return tk('type_service');
      default: return type;
    }
  }, [tk]);

  const listStats = useMemo(() => [
    { key: 'total', title: tk('stat_categories'), value: stats.total, icon: FolderOpen, color: 'from-blue-500 to-blue-600' },
    { key: 'active', title: tk('stat_active'), value: stats.active, icon: CheckCircle, color: 'from-green-500 to-green-600' },
    { key: 'inactive', title: tk('stat_inactive'), value: stats.inactive, icon: XCircle, color: 'from-red-500 to-red-600' },
    { key: 'products', title: tk('stat_products'), value: stats.productCategories, icon: Package, color: 'from-purple-500 to-purple-600' },
    { key: 'totalProducts', title: tk('stat_total_products'), value: stats.totalProducts, icon: Box, color: 'from-amber-500 to-amber-600', className: 'max-lg:hidden' },
  ], [stats, tk]);

  const qawaemTypeOptions = useMemo(() => [
    { value: 'all', label: tk('all_types') },
    { value: 'product', label: tk('type_product') },
    { value: 'service', label: tk('type_service') },
  ], [tk]);

  const qawaemTypeLabel = typeFilter === 'all'
    ? tk('filter_by_type_placeholder')
    : (qawaemTypeOptions.find((o) => o.value === typeFilter)?.label || tk('filter_by_type'));

  const qawaemBaseColumns = useMemo(() => ensureStatusBeforeActions([
    { key: 'category_number', label: tk('col_category_number'), required: true },
    { key: 'name', label: tk('col_name'), required: true },
    { key: 'description', label: tk('col_description') },
    { key: 'type', label: tk('col_type') },
    { key: 'product_count', label: tk('col_products') },
    { key: 'service_count', label: tk('col_services') },
    { key: 'status', label: tk('col_status') },
  ]), [tk]);
  const { handleToggleColumn, showCol, pickerColumns, visibleColumnCount } = useQawaemTableColumnVisibility(qawaemBaseColumns);
  const qawaemColumns = useMemo(
    () => withQawaemActionsColumn(pickerColumns, tk('col_actions')),
    [pickerColumns, tk],
  );
  const visibleTableColCount = useMemo(
    () => countVisibleTableCols(qawaemBaseColumns, showCol, { extra: 1 }),
    [qawaemBaseColumns, showCol],
  );

  const renderLinkedCountCell = (count) => (
    count > 0 ? (
      <span className="font-semibold text-green-400">{count}</span>
    ) : (
      <span className="text-white/30">—</span>
    )
  );

  const getViewingLinkedCounts = (record) => {
    const hit = categoriesEnriched.find((c) => c.id === record?.id);
    return {
      products: hit?.product_linked_count || countLinkedForCategory(record, 'product'),
      services: hit?.service_linked_count || countLinkedForCategory(record, 'service'),
    };
  };

  return (
    <>
      <FacturationListPageLayout
        footer={(
          <FacturationListPaginationBar
            totalItems={facturationListTotal}
            rawPage={facturationListPage}
            onPageChange={setFacturationListPage}
            pageSize={facturationPageSize}
          />
        )}
      className="space-y-6"
      containerClassName="employees-container"
      title={tk('title')}
      subtitle={tk('subtitle')}
      icon={FolderOpen}
      stats={listStats}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder={tk('search_placeholder')}
      onCreate={openNewCategoryModal}
      createLabel={tk('new_category')}
      importExport={{
        importOpen: showImportDropdown,
        exportOpen: showExportDropdown,
        onToggleImport: () => { setShowImportDropdown(!showImportDropdown); setShowExportDropdown(false); },
        onToggleExport: () => { setShowExportDropdown(!showExportDropdown); setShowImportDropdown(false); },
        importRef: importDropdownRef,
        exportRef: exportDropdownRef,
        importLabel: tk('import_label'),
        exportLabel: tk('export_label'),
        generatingLabel: tk('generating'),
        isGeneratingPDF: isGeneratingPDF,
        onImportCSV: handleImportCSV,
        onImportExcel: handleImportExcel,
        onImportJSON: handleImportJSON,
        onImportXML: handleImportXML,
        onImportTXT: handleImportTXT,
        onExportPDF: handleExportPDF,
        onExportExcel: handleExportExcel,
        onExportCSV: handleExportCSV,
        onExportJSON: handleExportJSON,
        onExportXML: handleExportXML,
        onExportHTML: handleExportHTML,
        onExportTXT: handleExportTXT,
      }}
      showAdvancedOptions={showAdvancedOptions}
      onToggleAdvancedOptions={() => setShowAdvancedOptions(!showAdvancedOptions)}
      advancedOptionsLabel={tk('advanced_options')}
      advancedOptionsContent={(
        <div className="space-y-6 pt-6">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button key={tab.id} onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                  <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>
          {mainViewMode === 'all_categories' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button key={filter.id} onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                    <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
              {activeDistributionFilter && (
                <button 
                  onClick={() => setActiveDistributionFilter(null)}
                  className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1"
                >
                  <XCircle size={14} />
                  {tk('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '3')}>
              {distributionInfo.data.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveDistributionFilter(activeDistributionFilter === item.id ? null : item.id)}
                  className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${
                    activeDistributionFilter === item.id 
                      ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' 
                      : 'border-white/10 hover:border-white/20 hover:bg-white/10'
                  }`}
                  title={tk('filter_by_tooltip', { name: item.name })}
                >
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === item.id ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    })()}%` }}></div>
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === item.id ? 'text-white/80' : 'text-white/60'}`}>{(() => {
                    const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                    const percent = total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    return tk('percent_of_categories', { percent });
                  })()}</p>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      )}
      controlsStrip={(
        <div className="facturation-list-controls-strip p-4 border-b border-white/10">
          <div className="facturation-list-controls-row flex min-w-0 flex-row flex-wrap items-center gap-2 lg:gap-4">
            {shouldShowCardsToolbarSearch(viewMode) ? (
              <div className="relative min-w-0 flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                <input type="text" placeholder={tk('search_placeholder')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            ) : null}
            <div className="facturation-list-toolbar ml-auto flex shrink-0 items-center gap-2 flex-wrap">
              
              {/* View Mode Toggle */}
              <FacturationViewModeToggle
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                isLight={isDashboardLightMode}
                cardsTitle={tk('view_cards')}
                tableTitle={tk('view_table')}
              />
              
              <div className="relative" ref={dateRangeRef}>
                <button 
                  type="button"
                  onClick={() => setIsDateRangeOpen(!isDateRangeOpen)} 
                  title={tk('filter_period')}
                  aria-label={tk('filter_period')}
                  className="flex items-center gap-2 px-4 py-2 border border-white/20 rounded-lg text-white hover:border-white/30 transition-all bg-white/5"
                >
                  <Calendar size={18} className="text-white/80 shrink-0" />
                  <span className="hidden sm:inline text-white font-medium text-sm">{tk('period')}</span>
                  <ChevronDown size={14} className={`hidden sm:block shrink-0 text-white/70 transition-transform ${isDateRangeOpen ? 'rotate-180' : ''}`} />
                </button>
                {isDateRangeOpen && (
                  <div 
                    className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[320px]" 
                    style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 99999, backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))', boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)', overflow: 'visible' }}
                  >
                    <div className="p-4" style={{ overflow: 'visible' }}>
                      <div className="flex flex-col gap-3" style={{ overflow: 'visible' }}>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" style={{ overflow: 'visible' }}>
                          <div style={{ overflow: 'visible' }}>
                            <label className="text-white/80 text-xs mb-1.5 block w-full font-medium text-right">{tk('start_date')}</label>
                            <DatePicker
                              selected={dateRange.start}
                              onChange={(date) => setDateRange({...dateRange, start: date})}
                              dateFormat="dd/MM/yyyy"
                              placeholderText={tk('select')}
                              locale={datePickerLocale}
                              className="w-full px-3 py-2 bg-white/10 border border-blue-400/30 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400/60 backdrop-blur-sm"
                              wrapperClassName="w-full"
                              portalId="qawaem-datepicker-portal"
                              popperClassName="date-picker-popper-high-z"
                              popperProps={{
                                strategy: 'fixed'
                              }}
                            />
                          </div>
                          <div style={{ overflow: 'visible' }}>
                            <label className="text-white/80 text-xs mb-1.5 block w-full font-medium text-right">{tk('end_date')}</label>
                            <DatePicker
                              selected={dateRange.end}
                              onChange={(date) => setDateRange({...dateRange, end: date})}
                              dateFormat="dd/MM/yyyy"
                              placeholderText={tk('select')}
                              minDate={dateRange.start}
                              locale={datePickerLocale}
                              className="w-full px-3 py-2 bg-white/10 border border-blue-400/30 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400/60 backdrop-blur-sm"
                              wrapperClassName="w-full"
                              portalId="qawaem-datepicker-portal"
                              popperClassName="date-picker-popper-high-z"
                              popperProps={{
                                strategy: 'fixed'
                              }}
                            />
                          </div>
                        </div>
                        <div className="flex gap-2 pt-2 border-t border-blue-400/30">
                          <button onClick={() => setDateRange({ start: null, end: null })} className="flex-1 px-3 py-2 bg-white/5 hover:bg-blue-500/20 text-white/80 hover:text-white rounded-xl text-sm transition-all font-medium">{tk('reset')}</button>
                          <button onClick={() => setIsDateRangeOpen(false)} className="flex-1 px-3 py-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl text-sm transition-all font-medium shadow-lg shadow-blue-500/30">{tk('apply')}</button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Type Filter */}
              <div className="relative" ref={typeFilterRef}>
                <button 
                  type="button"
                  onClick={() => setIsTypeFilterOpen(!isTypeFilterOpen)} 
                  title={typeFilter === 'all' ? tk('filter_by_type') : tk('type_colon', { type: getTypeLabel(typeFilter) })}
                  aria-label={typeFilter === 'all' ? tk('filter_by_type_aria') : tk('type_colon', { type: getTypeLabel(typeFilter) })}
                  className="flex items-center gap-2 px-4 py-2 border border-white/20 rounded-lg text-white hover:border-white/30 transition-all bg-white/5"
                >
                  <Tag size={18} className="text-white/80 shrink-0" />
                  <span className={`hidden sm:inline text-sm font-medium ${typeFilter === 'all' ? 'text-white/50' : 'text-white'}`}>
                    {typeFilter === 'all' ? tk('filter_by_type_placeholder') : getTypeLabel(typeFilter)}
                  </span>
                  <ChevronDown size={14} className={`hidden sm:block shrink-0 text-white/70 transition-transform ${isTypeFilterOpen ? 'rotate-180' : ''}`} />
                </button>
                {isTypeFilterOpen && (
                  <div 
                    className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[200px]" 
                    style={{ 
                      position: 'absolute', 
                      top: 'calc(100% + 8px)', 
                      right: 0, 
                      zIndex: 99999, 
                      backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))',
                      boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)' 
                    }}
                  >
                    <div className="p-2">
                      <button
                        onClick={() => { setTypeFilter('all'); setIsTypeFilterOpen(false); }}
                        className={`w-full px-4 py-2.5 text-left transition-colors flex items-center gap-3 rounded-xl ${typeFilter === 'all' ? 'bg-blue-500/20 text-white' : 'text-white/90 hover:bg-blue-500/20'}`}
                      >
                        <div className="w-2 h-2 rounded-full bg-white/50"></div>
                        <span className="text-sm font-medium">{tk('all_types')}</span>
                      </button>
                      {['product', 'service'].map(type => (
                        <button
                          key={type}
                          onClick={() => { setTypeFilter(type); setIsTypeFilterOpen(false); }}
                          className={`w-full px-4 py-2.5 text-left transition-colors flex items-center gap-3 rounded-xl ${typeFilter === type ? 'bg-blue-500/20 text-white' : 'text-white/90 hover:bg-blue-500/20'}`}
                        >
                          <div className="w-2 h-2 rounded-full bg-blue-400"></div>
                          <span className="text-sm font-medium">{getTypeLabel(type)}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {selectedRecords.length > 0 && (
                <button 
                  onClick={openBulkDeleteCategories}
                  className="flex items-center gap-2 px-3 py-2 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 text-red-200 rounded-lg transition-all text-sm font-medium"
                  title={tk('delete_selection')}
                >
                  <Trash2 size={16} />
                  <span className="hidden sm:inline">{tk('delete_count', { count: selectedRecords.length })}</span>
                </button>
              )}
              
              {/* Import Dropdown */}
              <div className="relative" style={{ zIndex: 100001 }} ref={importDropdownRef}>
                <button
                  type="button"
                  onClick={() => {
                    setShowImportDropdown(!showImportDropdown);
                    setShowExportDropdown(false);
                  }}
                  title={tk('import')}
                  aria-label={tk('import')}
                  className="bg-orange-500/20 hover:bg-orange-500/30 text-orange-200 border border-orange-500/30 px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-1.5"
                >
                  <Upload size={16} className="shrink-0" />
                  <span className="hidden sm:inline text-sm font-medium">{tk('import_label')}</span>
                  <ChevronDown size={14} className={`hidden sm:block shrink-0 transition-transform ${showImportDropdown ? 'rotate-180' : ''}`} />
                </button>
                {showImportDropdown && (
                  <div 
                    className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[120px]" 
                    style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 100002, overflow: 'visible', backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))', boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)' }}
                  >
                    <div className="p-2">
                      <button onClick={handleImportCSV} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-red-400" /><span className="text-sm font-medium">{t('common.format_csv')}</span></button>
                      <button onClick={handleImportExcel} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileSpreadsheet size={16} className="text-green-400" /><span className="text-sm font-medium">{t('common.format_excel')}</span></button>
                      <button onClick={handleImportJSON} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-yellow-400" /><span className="text-sm font-medium">JSON</span></button>
                      <button onClick={handleImportXML} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-orange-400" /><span className="text-sm font-medium">XML</span></button>
                      <button onClick={handleImportTXT} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-gray-400" /><span className="text-sm font-medium">TXT/TSV</span></button>
                    </div>
                  </div>
                )}
              </div>

              {/* Export Dropdown */}
              <div className="relative" style={{ zIndex: 100001 }} ref={exportDropdownRef}>
                <button
                  type="button"
                  onClick={() => {
                    setShowExportDropdown(!showExportDropdown);
                    setShowImportDropdown(false);
                  }}
                  title={tk('export')}
                  aria-label={tk('export')}
                  className="bg-green-500/20 hover:bg-green-500/30 text-green-200 border border-green-500/30 px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-1.5"
                >
                  <Download size={16} className="shrink-0" />
                  <span className="hidden sm:inline text-sm font-medium">{tk('export_label')}</span>
                  <ChevronDown size={14} className={`hidden sm:block shrink-0 transition-transform ${showExportDropdown ? 'rotate-180' : ''}`} />
                </button>
                {showExportDropdown && (
                  <div 
                    className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[120px]" 
                    style={{ 
                      position: 'absolute', 
                      top: 'calc(100% + 8px)', 
                      right: 0, 
                      zIndex: 100002,
                      overflow: 'visible',
                      backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))',
                      boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)' 
                    }}
                  >
                    <div className="p-2">
                      <button onClick={handleExportPDF} disabled={isGeneratingPDF} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl disabled:opacity-50"><FileText size={16} className="text-blue-400" /><span className="text-sm font-medium">{isGeneratingPDF ? tk('generating') : t('common.format_pdf')}</span></button>
                      <button onClick={handleExportExcel} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileSpreadsheet size={16} className="text-green-400" /><span className="text-sm font-medium">{t('common.format_excel')}</span></button>
                      <button onClick={handleExportCSV} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileOutput size={16} className="text-purple-400" /><span className="text-sm font-medium">{t('common.format_csv')}</span></button>
                      <button onClick={handleExportJSON} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-yellow-400" /><span className="text-sm font-medium">JSON</span></button>
                      <button onClick={handleExportXML} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-orange-400" /><span className="text-sm font-medium">XML</span></button>
                      <button onClick={handleExportHTML} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-pink-400" /><span className="text-sm font-medium">HTML</span></button>
                      <button onClick={handleExportTXT} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-gray-400" /><span className="text-sm font-medium">{t('common.format_txt')}</span></button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      filterActions={selectedRecords.length > 0 ? (
        <button
          type="button"
          onClick={openBulkDeleteCategories}
          className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-700 hover:bg-red-500/20 transition-colors text-sm font-medium shrink-0"
        >
          <Trash2 size={16} />
          <span>{tk('delete_count', { count: selectedRecords.length })}</span>
        </button>
      ) : null}
    >
        {/* Table View */}
        {viewMode === 'table' && (
          <div className={facturationTableScrollClass(qawaem)}>
            <table className="w-full employees-table data-table">
              <thead className={facturationTableHeadClass(qawaem)}>
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={allRowsSelected}
                        indeterminate={someRowsSelected}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>

                  {showCol('category_number') ? <TableColumnHeaderFilter columnKey="category_number" label={tk('col_category_number')} value={colFilters.category_number ?? ''} onChange={(v) => setColFilter('category_number', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                  <TableColumnHeaderFilter columnKey="created_at" label={tk('col_created_at')} value={colFilters.created_at ?? ''} onChange={(v) => setColFilter('created_at', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} />
                  {showCol('name') ? <TableColumnHeaderFilter columnKey="name" label={tk('col_name')} value={colFilters.name ?? ''} onChange={(v) => setColFilter('name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                  {showCol('description') ? <TableColumnHeaderFilter columnKey="description" label={tk('col_description')} value={colFilters.description ?? ''} onChange={(v) => setColFilter('description', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                  {showCol('type') ? <TableColumnHeaderFilter columnKey="type" label={tk('col_type')} value={colFilters.type ?? ''} onChange={(v) => setColFilter('type', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                  {showCol('product_count') ? <TableColumnHeaderFilter columnKey="product_count" label={tk('col_products')} value={colFilters.product_count ?? ''} onChange={(v) => setColFilter('product_count', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                  {showCol('service_count') ? <TableColumnHeaderFilter columnKey="service_count" label={tk('col_services')} value={colFilters.service_count ?? ''} onChange={(v) => setColFilter('service_count', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                  {showCol('status') ? <TableColumnHeaderFilter columnKey="status" label={tk('col_status')} kind="select" options={statusColumnFilterOptions} value={colFilters.status ?? ''} onChange={(v) => setColFilter('status', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-center" /> : null}
                  <QawaemTableHeaderActionsCell
                    label={tk('col_actions')}
                    columns={qawaemColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemColumns.filter((c) => c.checked).length}
                    totalCount={qawaemColumns.length}
                  thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredCategories.length > 0 ? (
                  pagedItems.map((record, index) => {
                    const productCount = record.product_linked_count || 0;
                    const serviceCount = record.service_linked_count || 0;
                    const linkedCount = record.linked_count || 0;

                    return (
                      <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                        <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                          <div className="flex justify-center">
                            <QawaemTableCheckbox
                              checked={selectedRecords.includes(record.id)}
                              onChange={() => handleSelectRecord(record.id)}
                            />
                          </div>
                        </td>

                        {showCol('category_number') ? (
                          <td className="px-6 py-4">
                            <CategoryEditableCell recordId={record.id} field="category_number" value={record.category_number} className="text-white font-semibold"
                              isEditing={editingCell?.recordId === record.id && editingCell?.field === 'category_number'}
                              editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                            />
                          </td>
                        ) : null}
                        
                        {/* Date de création — fixed column, not in picker */}
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white/80 text-sm">
                              {record.created_at ? new Date(record.created_at).toLocaleDateString(dateLocale) : '-'}
                            </span>
                            <span className="text-white/50 text-xs">
                              {record.created_at ? new Date(record.created_at).toLocaleTimeString(dateLocale, {hour: '2-digit', minute: '2-digit'}) : ''}
                            </span>
                          </div>
                        </td>
                        
                        {showCol('name') ? (
                          <td className="px-6 py-4">
                            <div className="flex flex-col">
                              <CategoryEditableCell recordId={record.id} field="name" value={record.name} className="text-white font-semibold"
                                isEditing={editingCell?.recordId === record.id && editingCell?.field === 'name'}
                                editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                              />
                              {linkedCount > 0 ? (
                                <span className="text-white/50 text-xs mt-0.5">
                                  {tk('linked_items_hint', { products: productCount, services: serviceCount })}
                                </span>
                              ) : null}
                            </div>
                          </td>
                        ) : null}
                        
                        {showCol('description') ? (
                          <td className="px-6 py-4">
                            <CategoryEditableCell recordId={record.id} field="description" value={record.description} className="text-white/80 text-sm"
                              isEditing={editingCell?.recordId === record.id && editingCell?.field === 'description'}
                              editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                            />
                          </td>
                        ) : null}
                        
                        {showCol('type') ? (
                          <td className="px-6 py-4">
                            <span className="text-white font-medium">{getTypeLabel(record.type)}</span>
                          </td>
                        ) : null}
                        
                        {showCol('product_count') ? (
                          <td className="px-6 py-4">
                            {renderLinkedCountCell(productCount)}
                          </td>
                        ) : null}

                        {showCol('service_count') ? (
                          <td className="px-6 py-4">
                            {renderLinkedCountCell(serviceCount)}
                          </td>
                        ) : null}
                        
                        {showCol('status') ? (
                          <td className="px-6 py-4 text-center">
                            <div className="flex justify-center">
                              <QawaemClickableStatusBadge
                                status={categoryStatusFromRecord(record)}
                              recordId={record.id}
                              options={statusOptions}
                              onChange={handleStatusChange}
                              title={tk('click_change_status')}
                            />
                          </div>
                        </td>
                        ) : null}
                        <QawaemTableStickyActionsTd>
                          <QawaemErpRowActions
                            onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                            onEdit={() => handleEdit(record)}
                            onDelete={() => openDeleteCategory(record.id)}
                            viewTitle={tk('view')}
                            editTitle={tk('edit')}
                            deleteTitle={tk('delete')}
                          />
                        </QawaemTableStickyActionsTd>
                      </tr>
                    );
                  })
                ) : (
                  <tr><td colSpan={visibleTableColCount} className="text-center py-12 text-white/60"><FolderOpen size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{tk('no_categories')}</p></td></tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-white/5 border-t border-white/10">
                  <td colSpan={visibleTableColCount} className="px-6 py-4">
                    <div className="flex items-center justify-between text-white/70 text-sm">
                      <span>{tk('footer_total', { count: filteredCategories.length })}</span>
                      <span>{tk('footer_linked_products', { count: categoriesFooter.produitsLiees })}</span>
                      <span>{tk('footer_linked_services', { count: categoriesFooter.servicesLiees })}</span>
                      <span>{tk('footer_active', { count: categoriesFooter.actives })}</span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {/* Card View */}
        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4 rounded-b-3xl">
            {filteredCategories.length > 0 ? (
              pagedItems.map((record, index) => (
                <motion.div
                  key={record.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-4 flex-wrap gap-y-2">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2 min-w-0">
                          <CategoryEditableCell recordId={record.id} field="name" value={record.name} className="text-xl font-bold text-white break-words"
                            isEditing={editingCell?.recordId === record.id && editingCell?.field === 'name'}
                            editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                          />
                        </h3>
                        <div className="flex"><QawaemClickableStatusBadge status={categoryStatusFromRecord(record)} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={tk('click_change_status')} /></div>
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-white/20 text-white/80">
                          {getTypeLabel(record.type)}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Informations */}
                        <div>
                          <h5 className="font-medium text-white mb-2">{tk('section_info')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{tk('label_description')}</span>{' '}
                              <CategoryEditableCell recordId={record.id} field="description" value={record.description} className="text-white/90 inline text-sm"
                                isEditing={editingCell?.recordId === record.id && editingCell?.field === 'description'}
                                editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                              />
                            </div>
                            <div><span className="font-medium">{tk('label_type')}</span> {getTypeLabel(record.type)}</div>
                            {(record.product_linked_count > 0 || record.service_linked_count > 0) ? (
                              <div><span className="font-medium">{tk('linked_items_hint', { products: record.product_linked_count || 0, services: record.service_linked_count || 0 })}</span></div>
                            ) : null}
                            {record.created_at && (
                              <div className="pt-2 border-t border-white/20">
                                <span className="font-medium">{tk('created_on')}</span>
                                <div className="text-white/90 text-xs mt-1">{new Date(record.created_at).toLocaleDateString(dateLocale)}</div>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Produits */}
                        {(record.product_linked_count > 0 || record.service_linked_count > 0) ? (
                        <div>
                          <h5 className="font-medium text-white mb-2">{tk('section_products')}</h5>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-white/70">
                            {record.product_linked_count > 0 ? (
                              <div className="p-3 rounded border bg-green-500/10 border-green-500/30">
                                <span className="font-medium text-xs">{tk('label_product_count')}</span>
                                <p className="text-2xl font-bold mt-1 text-green-400">{record.product_linked_count}</p>
                              </div>
                            ) : null}
                            {record.service_linked_count > 0 ? (
                              <div className="p-3 rounded border bg-purple-500/10 border-purple-500/30">
                                <span className="font-medium text-xs">{tk('label_service_count')}</span>
                                <p className="text-2xl font-bold mt-1 text-purple-300">{record.service_linked_count}</p>
                              </div>
                            ) : null}
                          </div>
                        </div>
                        ) : null}
                        
                        {/* Dates */}
                        <div>
                          <h5 className="font-medium text-white mb-2">{tk('section_dates')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div className="flex items-center gap-2">
                              <Clock size={16} className="text-blue-400" />
                              <span className="font-medium">{tk('created_on')}</span>
                              <span className="text-white/90">{record.created_at ? new Date(record.created_at).toLocaleDateString(dateLocale) : '-'}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => openDeleteCategory(record.id)}
                      viewTitle={tk('view')}
                      editTitle={tk('edit')}
                      deleteTitle={tk('delete')}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <FolderOpen size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{tk('no_categories')}</p>
              </div>
            )}
          </div>
        )}

      </FacturationListPageLayout>

      {/* View Modal */}
      {showViewModal && viewingRecord && ReactDOM.createPortal(
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4"
            onClick={() => setShowViewModal(false)}
            role="presentation"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className={`backdrop-blur-lg rounded-2xl p-6 max-h-[90vh] overflow-y-auto ${isDashboardLightMode ? 'qawaem-modal-light border' : 'border-2 border-blue-400/60 bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800'}`}
              style={isDashboardLightMode ? { boxShadow: '0 10px 40px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(15, 23, 42, 0.06)', width: '95%', maxWidth: '1100px' } : { boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '1100px' }}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className={`text-2xl font-bold ${isDashboardLightMode ? 'text-gray-900' : 'text-white'}`}>{tk('view_modal_title')}</h2>
                <button onClick={() => setShowViewModal(false)} className={isDashboardLightMode ? 'text-gray-500 hover:text-gray-800 transition-colors' : 'text-white/70 hover:text-white transition-colors'}>
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <div className="mb-4">
                    <div>
                      <h3 className="text-white font-semibold text-2xl"><CategoryEditableCell recordId={viewingRecord.id} field="name" value={viewingRecord.name} className="text-white font-semibold text-2xl"
                        isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'name'}
                        editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                      /></h3>
                      <p className="text-white/60 text-sm">{getTypeLabel(viewingRecord.type)}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    <div>
                      <p className="text-white/60 text-sm mb-1">{tk('col_description')}</p>
                      <div className="text-white font-medium"><CategoryEditableCell recordId={viewingRecord.id} field="description" value={viewingRecord.description} className="text-white font-medium"
                        isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'description'}
                        editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                      /></div>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{tk('col_category_number')}</p>
                      <p className="text-white font-medium">{viewingRecord.category_number || '—'}</p>
                    </div>
                    {(() => {
                      const { products: viewProducts, services: viewServices } = getViewingLinkedCounts(viewingRecord);
                      return (
                        <>
                          {viewProducts > 0 ? (
                            <div>
                              <p className="text-white/60 text-sm mb-1">{tk('label_product_count')}</p>
                              <p className="font-bold text-2xl text-green-400">{viewProducts}</p>
                            </div>
                          ) : null}
                          {viewServices > 0 ? (
                            <div>
                              <p className="text-white/60 text-sm mb-1">{tk('label_service_count')}</p>
                              <p className="font-bold text-2xl text-purple-300">{viewServices}</p>
                            </div>
                          ) : null}
                        </>
                      );
                    })()}
                    <div>
                      <p className="text-white/60 text-sm mb-1">{tk('col_status')}</p>
                      <div className="flex"><QawaemClickableStatusBadge status={categoryStatusFromRecord(viewingRecord)} recordId={viewingRecord.id} options={statusOptions} onChange={handleStatusChange} title={tk('click_change_status')} /></div>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{tk('col_type')}</p>
                      <p className="text-white font-medium">{getTypeLabel(viewingRecord.type)}</p>
                    </div>
                    {viewingRecord.created_at && (
                      <div>
                        <p className="text-white/60 text-sm mb-1">{tk('label_creation_date')}</p>
                        <p className="text-white font-medium">{new Date(viewingRecord.created_at).toLocaleString(dateLocale)}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}

      {/* Create/Edit Modal */}
      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={resetForm} role="presentation">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className={`backdrop-blur-lg rounded-2xl max-h-[90vh] flex flex-col overflow-hidden ${isDashboardLightMode ? 'qawaem-modal-light border' : 'border-2 border-blue-400/60 bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800'}`} style={isDashboardLightMode ? { boxShadow: '0 10px 40px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(15, 23, 42, 0.06)', width: '90%', maxWidth: '1200px' } : { boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }} onClick={(e) => e.stopPropagation()}>
            <div className={`flex items-center justify-between p-6 border-b ${isDashboardLightMode ? 'border-gray-200' : 'border-white/10'}`}>
              <h2 className={`text-2xl font-bold ${isDashboardLightMode ? 'text-gray-900' : 'text-white'}`}>{editingRecord ? tk('edit_title') : tk('create_title')}</h2>
              <div className="flex items-center gap-3">
                <button type="submit" form="category-form" className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30">
                  <CheckCircle size={18} />
                  {editingRecord ? tk('update') : tk('create')}
                </button>
                <button onClick={resetForm} className={isDashboardLightMode ? 'text-gray-500 hover:text-gray-800 transition-colors' : 'text-white/70 hover:text-white transition-colors'}><X size={24} /></button>
              </div>
            </div>

            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="category-form" className={ERP_MODAL_FORM_CLASS} onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="min-w-0">
                    <label className={erpFormLabelClass(isDashboardLightMode)}>
                      <RequiredLabelText text={tk('label_category_number')} required />
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.category_number || ''}
                      onChange={(e) => setFormData({ ...formData, category_number: e.target.value })}
                      className="w-full min-h-[2.75rem] bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-mono"
                      placeholder={tk('placeholder_category_number')}
                      dir="ltr"
                    />
                  </div>
                  <div className="min-w-0">
                    <label className={erpFormLabelClass(isDashboardLightMode)}>
                      <RequiredLabelText text={tk('label_category_name')} required />
                    </label>
                    <input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full min-h-[2.75rem] bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={tk('placeholder_name_example')} />
                  </div>
                  <div className="min-w-0">
                    <label className={erpFormLabelClass(isDashboardLightMode)}>{tk('col_type')}</label>
                    <div className="relative" ref={typeDropdownRef}>
                      <button
                        type="button"
                        data-category-type-dropdown-button="true"
                        onClick={() => setShowTypeDropdown(!showTypeDropdown)}
                        className="w-full min-h-[2.75rem] bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-base text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all flex items-center justify-between gap-2"
                      >
                        <span className={`flex-1 truncate text-start ${formData.type ? '' : 'text-white/45'}`}>
                          {formData.type ? getTypeLabel(formData.type) : tk('select_type')}
                        </span>
                        <ChevronDown size={16} className={`shrink-0 transition-transform ${showTypeDropdown ? 'rotate-180' : ''}`} />
                      </button>
                      {showTypeDropdown && ReactDOM.createPortal(
                        <div
                          data-category-type-dropdown="true"
                          className="border border-blue-400/60 rounded-2xl shadow-2xl overflow-hidden"
                          style={{
                            backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))',
                            position: 'fixed',
                            ...(typeDropdownRef.current && (() => {
                              const rect = typeDropdownRef.current.getBoundingClientRect();
                              const spaceBelow = window.innerHeight - rect.bottom;
                              const spaceAbove = rect.top;
                              const dropdownHeight = 150;

                              if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
                                return { bottom: window.innerHeight - rect.top + 8, left: rect.left, width: rect.width };
                              }
                              return { top: rect.bottom + 8, left: rect.left, width: rect.width };
                            })()),
                            zIndex: 99999,
                            boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)',
                          }}
                        >
                          {['product', 'service'].map(type => (
                            <button key={type} type="button" onClick={() => { setFormData({ ...formData, type }); setShowTypeDropdown(false); }} className="w-full flex items-center px-4 py-2.5 hover:bg-blue-500/20 transition-all text-start text-white text-sm font-medium">
                              {getTypeLabel(type)}
                            </button>
                          ))}
                        </div>,
                        document.body
                      )}
                    </div>
                  </div>
                  <div className="min-w-0 md:col-span-2">
                    <label className={erpFormLabelClass(isDashboardLightMode)}>{tk('col_description')}</label>
                    <input type="text" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="w-full min-h-[2.75rem] bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all text-sm" placeholder={tk('placeholder_description')} />
                  </div>
                  <div className="min-w-0">
                    <label className={erpFormLabelClass(isDashboardLightMode)}>{tk('col_status')}</label>
                    <CustomSelect
                      value={formData.status}
                      onChange={(value) =>
                        setFormData({ ...formData, status: value, active: value === 'active' })
                      }
                      options={categoryModalStatusSelectOptions}
                      placeholder={tk('select_status')}
                      searchable={false}
                      creatable={false}
                    />
                  </div>
                </div>
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}
      <AppConfirmDialog
        open={deleteConfirm.open}
        title={
          deleteConfirm.ids.length > 1
            ? t('common.delete', 'حذف')
            : `${t('common.delete', 'حذف')} — ${entityLabel}`
        }
        message={
          deleteConfirm.ids.length > 1
            ? tk('bulk_delete_confirm', { count: deleteConfirm.ids.length })
            : tk('delete_confirm')
        }
        confirmLabel={t('common.delete', 'حذف')}
        cancelLabel={t('common.cancel', 'إلغاء')}
        danger
        onCancel={closeDeleteConfirm}
        onConfirm={executeDeleteCategories}
      />
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </>
  );
};

export default Categories;


